// Acceso al backend.
//
// pedir() es el transporte, compartido por la página pública y el panel;
// las rutas de cada uno viven acá abajo (api.*) y en api/panel.js.
//
// Regla de este archivo: hacia afuera nunca sale un código HTTP ni un mensaje
// técnico. Cada falla se traduce a algo que una persona pueda leer y accionar.
// El detalle real queda en la consola para quien esté depurando.

export class ErrorApi extends Error {
  constructor(mensaje, { tipo = 'desconocido', status = 0, codigo = null } = {}) {
    super(mensaje);
    this.name = 'ErrorApi';
    this.tipo = tipo;      // 'red'|'sesion'|'prohibido'|'no_encontrado'|'ocupado'|'datos'|'limite'|'servidor'|'trial_vencido'
    this.status = status;
    // El código del backend, tal cual. `tipo` agrupa por cómo reacciona la
    // interfaz; esto identifica el caso exacto, y hay pantallas que necesitan
    // distinguir dos conflictos que comparten el mismo status.
    this.codigo = codigo;
  }
}

const MENSAJES = {
  red: 'No pudimos conectarnos. Fijate si tenés internet y probá de nuevo.',
  no_encontrado: 'No encontramos ese negocio.',
  ocupado: 'Ese horario se ocupó recién. Elegí otro, por favor.',
  servidor: 'Tuvimos un problema de nuestro lado. Probá de nuevo en un momento.'
};

export async function pedir(ruta, opciones = {}) {
  let respuesta;

  try {
    respuesta = await fetch(ruta, {
      headers: { accept: 'application/json', ...(opciones.body ? { 'content-type': 'application/json' } : {}) },
      ...opciones
    });
  } catch (error) {
    console.error('[api] sin conexión', error);
    throw new ErrorApi(MENSAJES.red, { tipo: 'red' });
  }

  let cuerpo = null;
  try {
    const texto = await respuesta.text();
    cuerpo = texto ? JSON.parse(texto) : null;
  } catch {
    cuerpo = null;
  }

  if (respuesta.ok) return cuerpo;

  console.error('[api] error', respuesta.status, cuerpo);

  // La sesión del panel se venció o nunca existió. Quien llame decide qué
  // hacer: el panel manda a login, el login lo muestra como credenciales
  // incorrectas.
  if (respuesta.status === 401) {
    throw new ErrorApi(cuerpo?.error?.mensaje || 'Tenés que volver a entrar.', { tipo: 'sesion', status: 401 });
  }

  // Cuenta suspendida. El backend manda un mensaje escrito para leerse.
  if (respuesta.status === 403) {
    throw new ErrorApi(cuerpo?.error?.mensaje || 'No tenés permisos para esto.', { tipo: 'prohibido', status: 403 });
  }

  // Se terminó la prueba gratis. Va con su propio tipo y no en el cajón de
  // "error del servidor": el backend manda un texto escrito para el dueño
  // ("Tu prueba terminó. Escribinos para reactivar la cuenta.") y pintarle
  // "tuvimos un problema de nuestro lado" es hacerle creer que el roto somos
  // nosotros cuando lo que hay que hacer es entrar a /panel/cuenta.
  if (respuesta.status === 402) {
    throw new ErrorApi(cuerpo?.error?.mensaje || 'Se terminó tu prueba.', {
      tipo: 'trial_vencido', status: 402
    });
  }

  if (respuesta.status === 404) {
    throw new ErrorApi(MENSAJES.no_encontrado, { tipo: 'no_encontrado', status: 404 });
  }

  if (respuesta.status === 409) {
    // El backend manda un mensaje pensado para el cliente final: se usa ese.
    // El código viaja aparte porque hay más de un conflicto posible y la
    // pantalla del registro tiene que distinguirlos: desempatar por el texto
    // del mensaje es lo que hacía que "estamos recibiendo muchas altas" se
    // pintara como si el email de la persona fuera el problema.
    throw new ErrorApi(cuerpo?.error?.mensaje || MENSAJES.ocupado, {
      tipo: 'ocupado', status: 409, codigo: cuerpo?.error?.codigo ?? null
    });
  }

  if (respuesta.status === 422) {
    const detalle = cuerpo?.error?.detalles?.[0]?.mensaje;
    throw new ErrorApi(detalle || cuerpo?.error?.mensaje || 'Revisá los datos e intentá de nuevo.', {
      tipo: 'datos', status: 422
    });
  }

  if (respuesta.status === 429) {
    throw new ErrorApi(cuerpo?.error?.mensaje || 'Probá de nuevo en un rato.', {
      tipo: 'limite', status: 429
    });
  }

  throw new ErrorApi(MENSAJES.servidor, { tipo: 'servidor', status: respuesta.status });
}

const base = (slug) => `/api/p/${encodeURIComponent(slug)}`;

export const api = {
  negocio: (slug) => pedir(base(slug)).then((r) => r.negocio),

  servicios: (slug) => pedir(`${base(slug)}/servicios`).then((r) => r.datos),

  profesionales: (slug, servicioId) => {
    const query = servicioId ? `?servicio=${encodeURIComponent(servicioId)}` : '';
    return pedir(`${base(slug)}/profesionales${query}`).then((r) => r.datos);
  },

  disponibilidad: (slug, { profesional, servicio, fecha }, opciones = {}) => {
    const query = new URLSearchParams({ profesional, servicio, fecha });
    return pedir(`${base(slug)}/disponibilidad?${query}`, opciones);
  },

  // Cómo salió el pago de la seña. Se pregunta al volver de Mercado Pago:
  // lo que diga la URL del checkout no alcanza, porque el que confirma el
  // turno es el webhook y puede llegar un instante después que la persona.
  estadoDelPago: (slug, turnoId) =>
    pedir(`${base(slug)}/pago/${encodeURIComponent(turnoId)}`),

  // Le pide al servidor que vaya a preguntarle a Mercado Pago por este pago.
  // El número sale de la URL de vuelta, pero no se le cree: es la llave para
  // ir a la fuente, y el servidor exige que ese pago sea de este turno.
  confirmarPago: (slug, turnoId, pagoId) =>
    pedir(`${base(slug)}/pago/${encodeURIComponent(turnoId)}/confirmar`, {
      method: 'POST',
      body: JSON.stringify({ payment_id: String(pagoId) })
    }),

  reservar: (slug, datos) => pedir(`${base(slug)}/reservar`, {
    method: 'POST',
    body: JSON.stringify(datos)
  })
};
